import React from 'react';
import { FormattedMessage } from 'react-intl';
import { IconButton, Tooltip } from '@mui/material';
import { Restore } from '@mui/icons-material';
import {
  SectionStyled,
  SectionHeaderStyled,
  SectionTitleStyled,
  SectionDescriptionStyled,
  LinearProgressStyled,
  NoDataMessageStyled,
} from '../RestoreConnectionsPage.styles';
import { FilterableTable } from 'app/components';
import type { FilterableTableColumnType, FilterableTableRowMapperFunc } from 'app/components';
import { TranslationKeys } from 'app/translations';
import type { DeletedConnection } from 'app/types';
import formatter from 'app/utils/formatter';

enum ConnectionsTableColumns {
  ConnectionName = 'connectionName',
  InsurerName = 'insurerName',
  CreatedAt = 'createdAt',
  UpdatedAt = 'updatedAt',
  Actions = 'actions',
}

interface ConnectionsTableProps {
  data: DeletedConnection[] | undefined;
  loading: boolean;
  onRestoreClick: (connection: DeletedConnection) => void;
  restoreLoading: boolean;
  selectedEmployerName: string;
  pagination: { page: number; rowsPerPage: number; totalCount: number };
  onPaginationChange: ({ page, rowSize }: { page?: number; rowSize?: number }) => void;
}

export const ConnectionsTable: React.FC<ConnectionsTableProps> = ({
  data,
  loading,
  onRestoreClick,
  restoreLoading,
  selectedEmployerName,
  pagination,
  onPaginationChange,
}) => {
  const connectionsTableHeaders = React.useMemo<Array<FilterableTableColumnType>>(() => {
    return [
      {
        name: ConnectionsTableColumns.ConnectionName,
        title: <FormattedMessage id={TranslationKeys.insurers_connectionName} />,
      },
      {
        name: ConnectionsTableColumns.InsurerName,
        title: <FormattedMessage id={TranslationKeys.insurers_insurerName} />,
      },
      {
        name: ConnectionsTableColumns.CreatedAt,
        title: <FormattedMessage id={TranslationKeys.global_createdAt} />,
      },
      {
        name: ConnectionsTableColumns.UpdatedAt,
        title: <FormattedMessage id={TranslationKeys.tools_restoreConnections_deletedAt} />,
      },
      {
        name: ConnectionsTableColumns.Actions,
        title: '',
      },
    ];
  }, []);

  const connectionsRowMapper = React.useCallback<FilterableTableRowMapperFunc<any>>(
    (data: DeletedConnection) => {
      return {
        data: {
          [ConnectionsTableColumns.ConnectionName]: data.connectionName,
          [ConnectionsTableColumns.InsurerName]: data.insurerName,
          [ConnectionsTableColumns.CreatedAt]: formatter.date(data.createdAt),
          [ConnectionsTableColumns.UpdatedAt]: formatter.date(data.updatedAt),
          [ConnectionsTableColumns.Actions]: (
            <Tooltip title={<FormattedMessage id={TranslationKeys.tools_restoreConnections_restore} />}>
              <IconButton color="primary" onClick={() => onRestoreClick(data)} disabled={restoreLoading}>
                <Restore />
              </IconButton>
            </Tooltip>
          ),
        },
      };
    },
    [onRestoreClick, restoreLoading],
  );

  return (
    <SectionStyled elevation={1}>
      <SectionHeaderStyled>
        <SectionTitleStyled variant="h6">
          <FormattedMessage id={TranslationKeys.tools_restoreConnections_deletedConnections} />
          {' - '}
          {selectedEmployerName}
        </SectionTitleStyled>
        <SectionDescriptionStyled variant="body2">
          <FormattedMessage id={TranslationKeys.tools_restoreConnections_deletedConnectionsDescription} />
        </SectionDescriptionStyled>
      </SectionHeaderStyled>

      {loading ? (
        <LinearProgressStyled />
      ) : data && data.length > 0 ? (
        <FilterableTable
          columns={connectionsTableHeaders}
          rows={data}
          loading={loading}
          rowMapper={connectionsRowMapper}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
        />
      ) : (
        <NoDataMessageStyled>
          <FormattedMessage id={TranslationKeys.tools_restoreConnections_noDeletedConnections} />
        </NoDataMessageStyled>
      )}
    </SectionStyled>
  );
};
